export interface FormatColumnDef {
  key: string
  label: string
  width?: number
  type: 'text' | 'number' | 'select' | 'currency'
  align?: 'left' | 'center' | 'right'
  options?: string[]
  /** Vai trò cột (đợt 4). Cột chưa khai báo thì không có. */
  role?: 'identity' | 'lookup' | 'compute' | 'info' | 'input' | null
  /** Khóa chuẩn mở rộng; các khóa mới lưu trong JSONB, không cần migration. */
  canonical_key?: string | null
  required?: boolean
  unit?: string
}

/**
 * Lựa chọn "vai trò hiển thị" cho một cột. Mỗi lựa chọn ánh xạ sang cặp
 * (role, canonical_key) mà server kiểm tra khi lưu mẫu.
 */
export const FORMAT_ROLE_OPTIONS: ReadonlyArray<{
  value: string
  label: string
  role: NonNullable<FormatColumnDef['role']>
  canonical_key: FormatColumnDef['canonical_key']
}> = [
  { value: 'identity', label: 'Tên hàng', role: 'identity', canonical_key: null },
  { value: 'spec', label: 'Quy cách / thông số', role: 'lookup', canonical_key: null },
  { value: 'color', label: 'Màu', role: 'lookup', canonical_key: 'color' },
  { value: 'uom', label: 'Đơn vị (ĐVT)', role: 'info', canonical_key: 'uom' },
  { value: 'price', label: 'Đơn giá', role: 'compute', canonical_key: 'price' },
  { value: 'qty', label: 'Số lượng', role: 'compute', canonical_key: null },
  { value: 'info', label: 'Chỉ mô tả', role: 'info', canonical_key: null },
  { value: 'input', label: 'Nhập lúc mua', role: 'input', canonical_key: null },
]

/** Giá trị lựa chọn hiện tại của một cột, '' nếu chưa khai báo. */
export function roleOptionOf(col: Pick<FormatColumnDef, 'role' | 'canonical_key'>): string {
  if (col.canonical_key === 'color') return 'color'
  if (col.canonical_key === 'uom') return 'uom'
  if (col.canonical_key === 'price') return 'price'
  if (col.role === 'identity') return 'identity'
  if (col.role === 'lookup') return 'spec'
  if (col.role === 'compute') return 'qty'
  if (col.role === 'info') return 'info'
  if (col.role === 'input') return 'input'
  return ''
}

/** Lỗi server từ chối mẫu vì thiếu vai trò bắt buộc (đợt 4). */
export class FormatRolesError extends Error {
  constructor(message: string) {
    super(message)
    this.name = 'FormatRolesError'
  }
}

export interface SupplierFormatDef {
  id: string
  code: string
  name: string
  tabLabel?: string
  uomDefault: string
  description: string
  formulaDescription: string
  isSystem: boolean
  /** Goods category this format belongs to (drives NCC Nhóm hàng). */
  categoryId: string
  categoryName: string
  columns: FormatColumnDef[]
  sampleRows: any[]
  pricingWorkbookVersionId?: string | null
}


/**
 * Stable column key from label at creation time only.
 * "QUY CÁCH" → col_quy_cach; "ĐỊNH LƯỢNG" → col_dinh_luong.
 * Never regenerate after create (label edits must not change key).
 */
export function slugifyColumnKey(label: string, existingKeys: Iterable<string>): string {
  const used = new Set(existingKeys)
  const base =
    label
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/đ/g, 'd')
      .replace(/Đ/g, 'd')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '_')
      .replace(/^_+|_+$/g, '') || 'col'
  let key = `col_${base}`
  let n = 2
  while (used.has(key)) {
    key = `col_${base}_${n++}`
  }
  return key
}

/** Stable category id/name for a newly created custom format. */
export function categoryFromFormatName(formatId: string, formatName: string): {
  categoryId: string
  categoryName: string
} {
  const slug = formatId
    .replace(/^new_/, '')
    .replace(/[^a-zA-Z0-9]+/g, '-')
    .replace(/^-|-$/g, '')
    .toLowerCase()
    .slice(0, 40)
  return {
    categoryId: `cat-${slug || 'custom'}`,
    categoryName: formatName.trim() || 'Khác',
  }
}

/** Unique goods categories derived from current formats. */
export function categoriesFromFormats(
  formats: SupplierFormatDef[],
): { id: string; code: string; name: string; isActive: boolean }[] {
  const map = new Map<string, { id: string; code: string; name: string; isActive: boolean }>()
  for (const f of formats) {
    if (!f.categoryId || !f.categoryName) continue
    if (map.has(f.categoryId)) continue
    const code = f.categoryId
      .replace(/^cat-/, '')
      .toUpperCase()
      .replace(/-/g, '_')
      .slice(0, 16)
    map.set(f.categoryId, {
      id: f.categoryId,
      code: code || 'CAT',
      name: f.categoryName,
      isActive: true,
    })
  }
  return [...map.values()].sort((a, b) => a.name.localeCompare(b.name, 'vi'))
}

/** Formats whose category is in supplier category_ids (or all if empty). */
export function filterFormatsBySupplierCategories(
  formats: SupplierFormatDef[],
  categoryIds: string[] | null | undefined,
): SupplierFormatDef[] {
  if (!categoryIds || categoryIds.length === 0) return formats
  const set = new Set(categoryIds)
  return formats.filter((f) => f.categoryId && set.has(f.categoryId))
}

export const WOOD_FORMAT_IDS = ['van_cao_su', 'phoi_go', 'van_ep', 'veneer'] as const
export type WoodFormatId = (typeof WOOD_FORMAT_IDS)[number]

export function isWoodFormat(formatId: string | null | undefined): boolean {
  if (!formatId) return false
  return (WOOD_FORMAT_IDS as readonly string[]).includes(formatId)
}

export const DEFAULT_SUPPLIER_FORMATS: SupplierFormatDef[] = [
  {
    id: 'van_cao_su',
    code: 'FMT-VAN-CAOSU',
    name: 'Ván cao su (AB/AC/BC/CC)',
    tabLabel: 'Ván cao su (AB/AC/BC/CC)',
    uomDefault: 'Tấm',
    description: 'Ván ghép thanh finger-joint mộng đứng/mộng nằm, chà nhám 2 mặt, báo giá theo 4 loại phẩm cấp.',
    formulaDescription: 'Khớp đơn giá theo phẩm cấp (AB / AC / BC / CC) × Số lượng tấm',
    isSystem: true,
    categoryId: 'cat-van',
    categoryName: 'Ván',
    columns: [
      { key: 'stt', label: 'STT', width: 48, type: 'number', align: 'center' },
      { key: 'spec', label: 'QUY CÁCH (DÀY*RỘNG*DÀI)', width: 220, type: 'text', align: 'left' },
      { key: 'grade', label: 'PHẨM CẤP', width: 140, type: 'select', align: 'center', options: ['AB', 'AC', 'BC', 'CC'] },
      { key: 'uom', label: 'ĐVT', width: 60, type: 'text', align: 'center' },
      { key: 'qty', label: 'SỐ LƯỢNG', width: 90, type: 'number', align: 'center' },
      { key: 'price', label: 'ĐƠN GIÁ (VNĐ/TẤM)', width: 145, type: 'currency', align: 'right' },
      { key: 'total', label: 'THÀNH TIỀN (VNĐ)', width: 145, type: 'currency', align: 'right' },
      { key: 'notes', label: 'GHI CHÚ KỸ THUẬT', width: 200, type: 'text', align: 'left' },
    ],
    sampleRows: [
      { spec: '12*1220*2440', grade: 'AB', uom: 'Tấm', qty: 40, priceAB: 609900, priceAC: 545700, priceBC: 524300, priceCC: 432000, price: 609900, notes: 'Chà nhám #180, mộng nằm' },
      { spec: '15*1220*2440', grade: 'AC', uom: 'Tấm', qty: 60, priceAB: 740000, priceAC: 660000, priceBC: 635000, priceCC: 520000, price: 660000, notes: 'Tiêu chuẩn xuất khẩu' },
      { spec: '18*1220*2440', grade: 'BC', uom: 'Tấm', qty: 30, priceAB: 865000, priceAC: 775000, priceBC: 745000, priceCC: 610000, price: 745000, notes: 'Chà nhám #240' },
      { spec: '20*1220*2440', grade: 'CC', uom: 'Tấm', qty: 50, priceAB: 955000, priceAC: 855000, priceBC: 820000, priceCC: 675000, price: 675000, notes: 'Gỗ sấy 10-12%' },
    ],
  },
  {
    id: 'phoi_go',
    code: 'FMT-PHOI-GO',
    name: 'Phôi gỗ (Tính m³)',
    tabLabel: 'Phôi gỗ (Tính m³)',
    uomDefault: 'm³',
    description: 'Phôi gỗ tự nhiên xẻ sấy (cao su, tràm, sồi, tần bì...), tính thể tích m³ từ kích thước 3 chiều.',
    formulaDescription: '(Dày × Rộng × Dài × Số lượng) / 10⁹ × Đơn giá m³',
    isSystem: true,
    categoryId: 'cat-go',
    categoryName: 'Gỗ',
    columns: [
      { key: 'stt', label: 'STT', width: 48, type: 'number', align: 'center' },
      { key: 'name', label: 'TÊN HÀNG / LOẠI GỖ', width: 220, type: 'text', align: 'left' },
      { key: 't', label: 'DÀY (mm)', width: 75, type: 'number', align: 'center' },
      { key: 'w', label: 'RỘNG (mm)', width: 85, type: 'number', align: 'center' },
      { key: 'l', label: 'DÀI (mm)', width: 85, type: 'number', align: 'center' },
      { key: 'uom', label: 'ĐVT', width: 55, type: 'text', align: 'center' },
      { key: 'qty', label: 'SỐ LƯỢNG', width: 85, type: 'number', align: 'center' },
      { key: 'vol', label: 'KHỐI LƯỢNG (m³)', width: 110, type: 'number', align: 'center' },
      { key: 'price', label: 'ĐƠN GIÁ (VNĐ/m³)', width: 135, type: 'currency', align: 'right' },
      { key: 'total', label: 'THÀNH TIỀN (VNĐ)', width: 135, type: 'currency', align: 'right' },
      { key: 'notes', label: 'GHI CHÚ KỸ THUẬT', width: 170, type: 'text', align: 'left' },
    ],
    sampleRows: [
      { name: 'PHÔI CAO SU AB', t: 21, w: 85, l: 800, wMin: 45, wMax: 105, lMin: 400, lMax: 1000, uom: 'm³', qty: 200, price: 7450000, notes: 'AB 80%' },
      { name: 'PHÔI CAO SU AB', t: 26, w: 95, l: 900, wMin: 45, wMax: 106, lMin: 400, lMax: 1000, uom: 'm³', qty: 150, price: 7550000, notes: 'AB 70%' },
      { name: 'PHÔI CAO SU ABC', t: 33, w: 100, l: 850, wMin: 45, wMax: 106, lMin: 400, lMax: 1000, uom: 'm³', qty: 120, price: 7650000, notes: 'Mộng đứng' },
    ],
  },
  {
    id: 'van_ep',
    code: 'FMT-VAN-EP',
    name: 'Ván ép (1220×2440)',
    tabLabel: 'Ván ép (1220×2440)',
    uomDefault: 'Tấm',
    description: 'Ván ép Plywood, MDF, MFC, HDF theo kích thước chuẩn 1220×2440mm, đơn giá tính theo tấm.',
    formulaDescription: 'Số lượng tấm × Đơn giá tấm theo độ dày (9mm, 12mm, 15mm, 18mm...)',
    isSystem: true,
    categoryId: 'cat-van',
    categoryName: 'Ván',
    columns: [
      { key: 'stt', label: 'STT', width: 48, type: 'number', align: 'center' },
      { key: 'name', label: 'TÊN HÀNG', width: 240, type: 'text', align: 'left' },
      { key: 't', label: 'DÀY (mm)', width: 85, type: 'number', align: 'center' },
      { key: 'w', label: 'RỘNG (mm)', width: 85, type: 'number', align: 'center' },
      { key: 'l', label: 'DÀI (mm)', width: 85, type: 'number', align: 'center' },
      { key: 'uom', label: 'ĐVT', width: 55, type: 'text', align: 'center' },
      { key: 'qty', label: 'SỐ LƯỢNG', width: 90, type: 'number', align: 'center' },
      { key: 'price', label: 'ĐƠN GIÁ (VNĐ/TẤM)', width: 140, type: 'currency', align: 'right' },
      { key: 'total', label: 'THÀNH TIỀN (VNĐ)', width: 140, type: 'currency', align: 'right' },
      { key: 'notes', label: 'GHI CHÚ', width: 180, type: 'text', align: 'left' },
    ],
    sampleRows: [
      { name: 'Ván ép thông AB', t: 9, w: 1220, l: 2440, uom: 'Tấm', qty: 50, price: 273000, notes: 'Lõi đặc phẳng' },
      { name: 'Ván ép thông AB', t: 12, w: 1220, l: 2440, uom: 'Tấm', qty: 30, price: 350200, notes: 'Khung định hình tủ' },
      { name: 'Ván ép thông AB', t: 15, w: 1220, l: 2440, uom: 'Tấm', qty: 25, price: 412000, notes: 'Dán cạnh keo E0' },
      { name: 'Ván ép thông AB', t: 18, w: 1220, l: 2440, uom: 'Tấm', qty: 20, price: 485000, notes: 'Lõi bạch đàn' },
    ],
  },
  {
    id: 'veneer',
    code: 'FMT-VENEER',
    name: 'Veneer (Tính m²)',
    tabLabel: 'Veneer (Tính m²)',
    uomDefault: 'm²',
    description: 'Veneer lạng tự nhiên và veneer kỹ thuật (Sồi, Óc chó, Tần bì...), đơn vị tính m² phân loại chiều dài ngắn/dài.',
    formulaDescription: 'Diện tích = (Rộng × Dài × Số lượng) / 10⁶; Thành tiền = Diện tích × Đơn giá m² (khớp theo L ≤ 1m hoặc L > 1m)',
    isSystem: true,
    categoryId: 'cat-van',
    categoryName: 'Ván',
    columns: [
      { key: 'stt', label: 'STT', width: 48, type: 'number', align: 'center' },
      { key: 'name', label: 'TÊN HÀNG / VENEER', width: 250, type: 'text', align: 'left' },
      { key: 't', label: 'DÀY (mm)', width: 75, type: 'number', align: 'center' },
      { key: 'w', label: 'RỘNG (mm)', width: 80, type: 'number', align: 'center' },
      { key: 'l', label: 'DÀI (mm)', width: 80, type: 'number', align: 'center' },
      { key: 'uom', label: 'ĐVT', width: 55, type: 'text', align: 'center' },
      { key: 'area', label: 'DIỆN TÍCH (m²)', width: 100, type: 'number', align: 'center' },
      { key: 'qty', label: 'SỐ LƯỢNG', width: 85, type: 'number', align: 'center' },
      { key: 'price', label: 'ĐƠN GIÁ (VNĐ/m²)', width: 135, type: 'currency', align: 'right' },
      { key: 'total', label: 'THÀNH TIỀN (VNĐ)', width: 135, type: 'currency', align: 'right' },
      { key: 'notes', label: 'GHI CHÚ', width: 170, type: 'text', align: 'left' },
    ],
    sampleRows: [
      { name: 'White Oak AA 0.52mm', t: 0.52, w: 150, l: 800, uom: 'm²', qty: 300, priceShort: 105000, priceLong: 140000, price: 105000, notes: 'L <= 1m' },
      { name: 'White Oak A 0.52mm', t: 0.52, w: 160, l: 1250, uom: 'm²', qty: 250, priceShort: 95000, priceLong: 135000, price: 135000, notes: 'L > 1m' },
      { name: 'Walnut AA 0.50mm', t: 0.50, w: 180, l: 1400, uom: 'm²', qty: 150, priceShort: 165000, priceLong: 210000, price: 210000, notes: 'Óc chó Bắc Mỹ tự nhiên' },
    ],
  },
  {
    id: 'ngu_kim',
    code: 'FMT-NGU-KIM',
    name: 'Ngũ kim & Phụ kiện',
    tabLabel: 'Ngũ kim & Phụ kiện',
    uomDefault: 'Bộ',
    description: 'Phụ kiện kim khí nội thất: ray bi 3 tầng, bản lề giảm chấn, tay nắm, ốc liên kết cam chốt.',
    formulaDescription: 'Số lượng (Bộ / Cái) × Đơn giá niêm yết',
    isSystem: true,
    categoryId: 'cat-nk',
    categoryName: 'Ngũ kim',
    columns: [
      { key: 'stt', label: 'STT', width: 48, type: 'number', align: 'center' },
      { key: 'name', label: 'TÊN PHỤ KIỆN / MÃ', width: 250, type: 'text', align: 'left' },
      { key: 'spec', label: 'QUY CÁCH / THÔNG SỐ', width: 180, type: 'text', align: 'left' },
      { key: 'color', label: 'MÀU', width: 120, type: 'text', align: 'center' },
      { key: 'uom', label: 'ĐVT', width: 60, type: 'text', align: 'center' },
      { key: 'qty', label: 'SỐ LƯỢNG', width: 85, type: 'number', align: 'center' },
      { key: 'price', label: 'ĐƠN GIÁ (VNĐ)', width: 135, type: 'currency', align: 'right' },
      { key: 'total', label: 'THÀNH TIỀN (VNĐ)', width: 135, type: 'currency', align: 'right' },
      { key: 'notes', label: 'GHI CHÚ KỸ THUẬT', width: 170, type: 'text', align: 'left' },
    ],
    sampleRows: [
      { name: 'Bản lề hơi giảm chấn Inox 304', spec: 'Thẳng, cong vừa, cong nhiều', brand: 'Hafele', uom: 'Pcs', qty: 200, price: 28500, notes: 'Bảo hành 2 năm' },
      { name: 'Ray bi 3 tầng giảm chấn 450mm', spec: 'Bản rộng 45mm, thép mạ kẽm', brand: 'Ivan', uom: 'Bộ', qty: 100, price: 68000, notes: 'Chịu tải 35kg' },
      { name: 'Ốc cam liên kết âm tủ', spec: 'Đường kính 15mm kèm chốt sắt', brand: 'DTC', uom: 'Bộ', qty: 1000, price: 2200, notes: 'Đóng túi 100 bộ' },
    ],
  },
  {
    id: 'carton',
    code: 'FMT-CARTON',
    name: 'Bao bì Carton',
    tabLabel: 'Bao bì Carton',
    uomDefault: 'Thùng',
    description: 'Thùng carton 3/5/7 lớp, tấm lót.',
    formulaDescription: 'Số lượng × Đơn giá',
    isSystem: true,
    categoryId: 'cat-bb',
    categoryName: 'Bao bì',
    columns: [
      { key: 'stt', label: 'STT', width: 48, type: 'number', align: 'center' },
      { key: 'name', label: 'LOẠI THÙNG', width: 230, type: 'text', align: 'left' },
      { key: 'dims', label: 'KÍCH THƯỚC', width: 160, type: 'text', align: 'center' },
      { key: 'flute', label: 'LOẠI SÓNG/GIẤY', width: 140, type: 'text', align: 'center' },
      { key: 'uom', label: 'ĐVT', width: 65, type: 'text', align: 'center' },
      { key: 'qty', label: 'SL', width: 75, type: 'number', align: 'right' },
      { key: 'price', label: 'ĐƠN GIÁ (VNĐ)', width: 130, type: 'currency', align: 'right' },
      { key: 'total', label: 'THÀNH TIỀN (VNĐ)', width: 140, type: 'currency', align: 'right' },
      { key: 'notes', label: 'GHI CHÚ', width: 180, type: 'text', align: 'left' },
    ],
    sampleRows: [
      { name: 'Thùng ghế ăn MP-C01', dims: '650×550×950', flute: 'BC 5 lớp', uom: 'Thùng', qty: 150, price: 34500, notes: 'In 2 màu' },
      { name: 'Thùng bàn ăn MP-T08', dims: '1850×950×220', flute: 'AB 7 lớp', uom: 'Thùng', qty: 80, price: 82000, notes: 'Chống thấm' },
      { name: 'Tấm lót đáy', dims: '1800×900', flute: 'B 3 lớp', uom: 'Tấm', qty: 300, price: 12000, notes: 'Đệm sốc' },
    ],
  },
  {
    id: 'thong_dung',
    code: 'FMT-THONG-DUNG',
    name: 'Hàng hóa thông dụng',
    tabLabel: 'Hàng hóa thông dụng',
    uomDefault: 'Pcs',
    description: 'Vật tư, hàng hóa phụ trợ thông dụng tính theo số lượng và đơn giá.',
    formulaDescription: 'Số lượng × Đơn giá',
    isSystem: true,
    categoryId: 'cat-thong-dung',
    categoryName: 'Hàng hóa thông dụng',
    columns: [
      { key: 'stt', label: 'STT', width: 48, type: 'number', align: 'center' },
      { key: 'name', label: 'TÊN HÀNG HÓA', width: 240, type: 'text', align: 'left' },
      { key: 'spec', label: 'QUY CÁCH / THÔNG SỐ', width: 180, type: 'text', align: 'left' },
      { key: 'uom', label: 'ĐVT', width: 60, type: 'text', align: 'center' },
      { key: 'qty', label: 'SL', width: 85, type: 'number', align: 'right' },
      { key: 'price', label: 'ĐƠN GIÁ (VNĐ)', width: 135, type: 'currency', align: 'right' },
      { key: 'total', label: 'THÀNH TIỀN (VNĐ)', width: 135, type: 'currency', align: 'right' },
      { key: 'notes', label: 'GHI CHÚ', width: 170, type: 'text', align: 'left' },
    ],
    sampleRows: [
      { name: 'Băng keo dán thùng 5cm (Vàng đục)', spec: 'Dày 50 mic, cuộn 100 yard', uom: 'Cuộn', qty: 120, price: 18500, notes: 'Đóng thùng 36 cuộn' },
      { name: 'Màng PE quấn pallet 2.4kg', spec: 'Khổ rộng 50cm, lõi 300g', uom: 'Cuộn', qty: 50, price: 85000, notes: 'Dẻo dai' },
      { name: 'Dây đai nhựa PP vàng', spec: 'Bản 12mm x 0.8mm (10kg/cuộn)', uom: 'Cuộn', qty: 10, price: 320000, notes: 'Dùng cho máy đai bán tự động' },
    ],
  },
  {
    id: 'mut_xop',
    code: 'mut_xop',
    name: 'Mút xốp',
    tabLabel: 'Mút xốp',
    uomDefault: 'm³',
    description: 'Vật tư mút xốp đệm chèn lót: xốp tấm, xốp góc, xốp L định hình, tỷ trọng/định lượng kg/m³.',
    formulaDescription: 'Số lượng × Đơn giá',
    isSystem: true,
    categoryId: 'cat-mut-xop',
    categoryName: 'Mút xốp',
    columns: [
      { key: 'stt', label: 'STT', width: 48, type: 'number', align: 'center' },
      { key: 'kind', label: 'LOẠI', width: 130, type: 'select', align: 'center', options: ['Xốp tấm', 'Xốp góc', 'Xốp L'] },
      { key: 'spec', label: 'QUY CÁCH', width: 180, type: 'text', align: 'left' },
      { key: 'density', label: 'ĐỊNH LƯỢNG', width: 120, type: 'text', align: 'center' },
      { key: 'uom', label: 'ĐVT', width: 65, type: 'text', align: 'center' },
      { key: 'price', label: 'ĐƠN GIÁ', width: 135, type: 'currency', align: 'right' },
      { key: 'notes', label: 'GHI CHÚ', width: 180, type: 'text', align: 'left' },
    ],
    sampleRows: [
      { kind: 'Xốp tấm', spec: '2000x1000x500', density: '10kg/m³', uom: 'm³', price: 587244, notes: 'Xốp khối cắt tấm' },
      { kind: 'Xốp góc', spec: '100x100x100x20', density: '16kg/m³', uom: 'Pcs', price: 920, notes: 'Góc 3 cạnh bảo vệ cạnh tủ' },
      { kind: 'Xốp L', spec: '1000x100x100x20', density: '12kg/m³', uom: 'Pcs', price: 3946, notes: 'Thanh nẹp L chống va đập' },
    ],
  },
]


function resolveCategoryFields(
  raw: Partial<SupplierFormatDef> & { id?: string; name?: string },
  fallback?: SupplierFormatDef,
): { categoryId: string; categoryName: string } {
  if (raw.categoryId && raw.categoryName) {
    return { categoryId: raw.categoryId, categoryName: raw.categoryName }
  }
  if (fallback?.categoryId && fallback.categoryName) {
    return { categoryId: fallback.categoryId, categoryName: fallback.categoryName }
  }
  const def = DEFAULT_SUPPLIER_FORMATS.find((d) => d.id === raw.id)
  if (def) {
    return { categoryId: def.categoryId, categoryName: def.categoryName }
  }
  return categoryFromFormatName(raw.id || 'custom', raw.name || fallback?.name || 'Khác')
}

let memoryFormats: SupplierFormatDef[] = [...DEFAULT_SUPPLIER_FORMATS]
const memorySupplierMapping: Record<string, string> = {}

export class SupplierFormatService {
  resetToDefaults(): void {
    memoryFormats = [...DEFAULT_SUPPLIER_FORMATS]
    for (const k of Object.keys(memorySupplierMapping)) {
      delete memorySupplierMapping[k]
    }
  }

  getAll(): SupplierFormatDef[] {
    return [...memoryFormats]
  }

  getById(id: string): SupplierFormatDef | undefined {
    return memoryFormats.find((f) => f.id === id)
  }

  async fetchFormats(supplierId?: string): Promise<SupplierFormatDef[]> {
    try {
      const url = `/api/suppliers/${supplierId ? encodeURIComponent(supplierId) : 'all'}/formats`
      const res = await fetch(url)
      if (res.ok) {
        const body = (await res.json()) as {
          ok: boolean
          formats?: any[]
          active_format_id?: string | null
        }
        if (body.ok && Array.isArray(body.formats)) {
          const prevById = new Map(memoryFormats.map((f) => [f.id, f]))
          const loaded: SupplierFormatDef[] = body.formats.map((f: any) => {
            const base = {
              id: String(f.id),
              code: f.code || '',
              name: f.name || f.format_name || '',
              tabLabel: f.tabLabel || f.tab_label || f.name,
              uomDefault: f.uomDefault || f.uom_default || '',
              description: f.description || '',
              formulaDescription: f.formulaDescription || f.formula_description || '',
              isSystem: Boolean(f.isSystem ?? f.is_system ?? false),
              columns: (f.columns || []) as FormatColumnDef[],
              sampleRows: (f.sampleRows || f.sample_rows || []) as any[],
              pricingWorkbookVersionId: f.pricingWorkbookVersionId ?? f.pricing_workbook_version_id ?? null,
              categoryId: f.categoryId || f.category_id,
              categoryName: f.categoryName || f.category_name,
            }
            const cat = resolveCategoryFields(base, prevById.get(String(f.id)))
            return { ...base, categoryId: cat.categoryId, categoryName: cat.categoryName }
          })

          const customLoaded = loaded.filter((l) => !l.isSystem)
          const systemLoaded = loaded.filter((l) => l.isSystem)
          memoryFormats =
            systemLoaded.length > 0
              ? [...systemLoaded, ...customLoaded]
              : [...DEFAULT_SUPPLIER_FORMATS, ...customLoaded]

          if (supplierId && body.active_format_id) {
            memorySupplierMapping[supplierId] = body.active_format_id
          }
        }
      }
    } catch {
      // Fallback to in-memory store
    }
    return [...memoryFormats]
  }

  async save(format: SupplierFormatDef, supplierId?: string): Promise<SupplierFormatDef> {
    const withCategory: SupplierFormatDef = {
      ...format,
      ...resolveCategoryFields(format),
    }
    const idx = memoryFormats.findIndex((f) => f.id === withCategory.id)
    const previousSnapshot = idx >= 0 ? { ...memoryFormats[idx] } : undefined
    if (idx >= 0) {
      memoryFormats[idx] = withCategory
    } else {
      memoryFormats.push(withCategory)
    }

    // Server từ chối (thiếu vai trò bắt buộc): trả bộ nhớ về trạng thái trước khi lưu
    // và báo lỗi, không để người dùng tưởng mẫu đã được lưu.
    const rejectLocalCopy = () => {
      const i = memoryFormats.findIndex((f) => f.id === withCategory.id)
      if (previousSnapshot) {
        if (i >= 0) memoryFormats[i] = previousSnapshot
      } else if (i >= 0) {
        memoryFormats.splice(i, 1)
      }
    }

    let resultFormat = withCategory

    try {
      const isExisting = withCategory.id && !withCategory.id.startsWith('new_')
      const url = `/api/suppliers/${supplierId ? encodeURIComponent(supplierId) : 'all'}/formats${
        isExisting ? `/${encodeURIComponent(withCategory.id)}` : ''
      }`
      const method = isExisting ? 'PUT' : 'POST'
      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...(isExisting ? { id: withCategory.id } : {}),
          name: withCategory.name,
          code: withCategory.code,
          tabLabel: withCategory.tabLabel,
          uomDefault: withCategory.uomDefault,
          description: withCategory.description,
          formulaDescription: withCategory.formulaDescription,
          categoryId: withCategory.categoryId,
          categoryName: withCategory.categoryName,
          columns: withCategory.columns,
          sampleRows: withCategory.sampleRows,
          pricingWorkbookVersionId: withCategory.pricingWorkbookVersionId ?? null,
          mappings: (withCategory.columns || []).map((col, cIdx) => ({
            excel_column: String.fromCharCode(65 + cIdx),
            field_code: col.key,
            field_name: col.label,
            is_required: ['name', 'kind', 'spec', 'uom', 'qty', 'price'].includes(col.key),
          })),
        }),
      })
      if (!res.ok) {
        const errBody = (await res.json().catch(() => null)) as
          | { error?: { code?: string; message?: string } }
          | null
        if (errBody?.error?.code === 'ROLES_INCOMPLETE') {
          throw new FormatRolesError(errBody.error.message || 'Mẫu chưa khai báo đủ vai trò cột.')
        }
      }
      if (res.ok) {
        const body = (await res.json()) as { ok: boolean; format?: any }
        if (body.ok && body.format) {
          const base = {
            id: String(body.format.id),
            code: body.format.code || '',
            name: body.format.name || body.format.format_name,
            tabLabel: body.format.tabLabel || body.format.tab_label,
            uomDefault: body.format.uomDefault || body.format.uom_default,
            description: body.format.description,
            formulaDescription:
              body.format.formulaDescription || body.format.formula_description,
            isSystem: Boolean(body.format.isSystem ?? body.format.is_system),
            columns: (body.format.columns || []) as FormatColumnDef[],
            sampleRows: (body.format.sampleRows || body.format.sample_rows || []) as any[],
            pricingWorkbookVersionId: body.format.pricingWorkbookVersionId ?? body.format.pricing_workbook_version_id ?? null,
            categoryId: body.format.categoryId || body.format.category_id,
            categoryName: body.format.categoryName || body.format.category_name,
          }
          const cat = resolveCategoryFields(base, withCategory)
          const saved: SupplierFormatDef = {
            ...base,
            categoryId: cat.categoryId,
            categoryName: cat.categoryName,
          }
          const savedIdx = memoryFormats.findIndex(
            (f) => f.id === withCategory.id || f.id === saved.id,
          )
          if (savedIdx >= 0) {
            memoryFormats[savedIdx] = saved
          } else {
            memoryFormats.push(saved)
          }
          resultFormat = saved
        }
      }
    } catch (err) {
      if (err instanceof FormatRolesError) {
        rejectLocalCopy()
        throw err
      }
      // Lỗi mạng: giữ bản trong bộ nhớ như trước
    }
    return resultFormat
  }

  async delete(id: string, supplierId?: string): Promise<boolean> {
    const target = this.getById(id)
    if (!target || target.isSystem) return false
    memoryFormats = memoryFormats.filter((f) => f.id !== id)

    try {
      const url = `/api/suppliers/${supplierId ? encodeURIComponent(supplierId) : 'all'}/formats/${encodeURIComponent(id)}`
      await fetch(url, { method: 'DELETE' })
    } catch {
      // Keep memory deletion
    }
    return true
  }

  getSupplierFormat(supplierId: string, defaultName = ''): string {
    if (memorySupplierMapping[supplierId]) {
      return memorySupplierMapping[supplierId]
    }
    // Auto-detect if not explicitly mapped
    const s = defaultName.toLowerCase()
    if (s.includes('cao su') || s.includes('thuận an')) return 'van_cao_su'
    if (s.includes('phôi') || s.includes('mộc phát')) return 'phoi_go'
    if (s.includes('ván ép') || s.includes('hải vân khánh') || s.includes('plywood')) return 'van_ep'
    if (s.includes('veneer') || s.includes('lạng việt') || s.includes('wan rui')) return 'veneer'
    if (s.includes('ngũ kim') || s.includes('ốc') || s.includes('phụ kiện')) return 'ngu_kim'
    if (s.includes('carton') || s.includes('bao bì') || s.includes('thùng')) return 'carton'
    if (s.includes('mút') || s.includes('xốp') || s.includes('đệm')) return 'mut_xop'
    return ''
  }

  async setSupplierFormat(supplierId: string, formatId: string): Promise<void> {
    memorySupplierMapping[supplierId] = formatId
    try {
      const url = `/api/suppliers/${encodeURIComponent(supplierId)}/formats/active`
      await fetch(url, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ format_id: formatId }),
      })
    } catch {
      // Keep memory version
    }
  }
}

export const supplierFormatService = new SupplierFormatService()
